import * as React from 'react';

import { type VariantProps } from '@/lib/tv';

import { textVariants } from './tokens/text-variants';

const variantElementMap: Partial<
  Record<
    NonNullable<VariantProps<typeof textVariants>['variant']>,
    React.ElementType
  >
> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  p: 'p',
  span: 'span',
  div: 'div',
  a: 'a',
  li: 'li',
  blockquote: 'blockquote',
  code: 'code',
  body: 'p',
  'body-semibold': 'p',
  large: 'h3',
  label: 'label',
  'label-semibold': 'label',
  caption: 'small',
  'caption-semibold': 'small',
};
const defaultVariantMap: Record<
  string,
  VariantProps<typeof textVariants>['variant']
> = {
  button: 'label',
  input: 'body',
  textarea: 'body',
};

type PolymorphicTextProps<T extends React.ElementType> = {
  as?: T;
  children?: React.ReactNode;
} & VariantProps<typeof textVariants> &
  Omit<
    React.ComponentPropsWithoutRef<T>,
    keyof VariantProps<typeof textVariants> | 'as'
  >;

type TextProps<T extends React.ElementType = 'span'> = PolymorphicTextProps<T>;

export const Text = <T extends React.ElementType = 'span'>({
  as,
  variant,
  color,
  weight,
  font,
  fontNumeric,
  noLineHeight,
  align,
  truncate,
  wrap,
  className,
  ...props
}: TextProps<T>) => {
  const Component =
    as ?? (variant ? variantElementMap[variant] : null) ?? 'span';
  const defaultVariant =
    typeof as === 'string' ? defaultVariantMap[as] : undefined;
  const effectiveVariant = variant ?? defaultVariant ?? 'label';

  return (
    <Component
      data-slot="text"
      className={textVariants({
        variant: effectiveVariant,
        color,
        weight,
        font,
        fontNumeric,
        noLineHeight,
        align,
        truncate,
        wrap,
        class: className,
      })}
      {...props}
    />
  );
};

Text.displayName = 'Text';
